import React from 'react';
import PropTypes from 'prop-types';
import { Badge } from 'react-bootstrap';
import { useIsMobile } from '../utils';

/* Shared pieces for the employee-side Entrepreneur admin pages: the palette,
 * the deep-blue page banner, card styling, and the hub-vs-Claude status
 * badges, so the sub-pages stay visually consistent. */

export const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  red:       '#c0392b',
};

export const usd = (cents) => `$${(Number(cents || 0) / 100).toFixed(2)}`;

export const cardStyle = { border: 'none', boxShadow: '0 2px 12px rgba(10,41,71,0.08)', borderRadius: 12, overflow: 'hidden' };
export const headStyle = { background: 'white', borderBottom: `1px solid ${brand.softGray}`, fontWeight: 700, color: brand.deepBlue };

// A firm's Claude sign-up is an optional track on top of hub membership, so
// staff-facing copy says what the application_status means for Claude rather
// than showing the raw value. DRAFT just means the firm never applied - most
// hub-only firms sit there for good, and that's not a problem to chase.
export const CLAUDE_TRACK = {
  DRAFT:     { label: 'Not signed up',  bg: 'light',     text: 'dark' },
  SUBMITTED: { label: 'In review',      bg: 'warning',   text: 'dark' },
  APPROVED:  { label: 'Approved',       bg: 'info',      text: 'dark' },
  ACTIVE:    { label: 'Active',         bg: 'success' },
  SUSPENDED: { label: 'Suspended',      bg: 'danger' },
  REJECTED:  { label: 'Not approved',   bg: 'danger' },
};
export const claudeSignedUp = (app) => !!app && app.application_status !== 'DRAFT';

export function ClaudeTrackBadge({ status, style }) {
  const t = CLAUDE_TRACK[status] || CLAUDE_TRACK.DRAFT;
  return <Badge bg={t.bg} text={t.text} style={{ border: t.bg === 'light' ? `1px solid ${brand.softGray}` : undefined, ...style }}>{t.label}</Badge>;
}
ClaudeTrackBadge.propTypes = { status: PropTypes.string, style: PropTypes.object };

// A firm still TRANSITIONING (working towards becoming an entrepreneur firm)
// sees only its transition dashboard until staff take it live. Nothing is
// shown for a live firm - that's the normal case.
export function StageBadge({ stage, style }) {
  if (stage !== 'transitioning') return null;
  return (
    <span style={{ background: 'rgba(208,155,44,0.18)', color: '#7a5a12', borderRadius: 20, padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap', ...style }}>
      Transitioning
    </span>
  );
}
StageBadge.propTypes = { stage: PropTypes.string, style: PropTypes.object };

// Per-member Claude seat state (b2b_profiles.provision_status). NONE = a hub
// member with no seat asked for, which is the normal case for most people.
export const SEAT_STATE = {
  NONE:      { label: 'Hub only',        fg: brand.charcoal, bg: 'rgba(88,88,91,0.08)' },
  REQUESTED: { label: 'Seat requested',  fg: '#b9770e',      bg: 'rgba(185,119,14,0.12)' },
  APPROVED:  { label: 'Seat approved',   fg: brand.tealBlue, bg: 'rgba(0,105,166,0.10)' },
  INVITED:   { label: 'In Claude',       fg: brand.green,    bg: 'rgba(26,127,78,0.12)' },
  ACTIVE:    { label: 'In Claude',       fg: brand.green,    bg: 'rgba(26,127,78,0.12)' },
  REJECTED:  { label: 'Seat rejected',   fg: brand.red,      bg: 'rgba(192,57,43,0.10)' },
  REVOKED:   { label: 'Seat revoked',    fg: brand.charcoal, bg: 'rgba(88,88,91,0.08)' },
};
export const SEAT_PENDING = ['REQUESTED', 'APPROVED'];
export const SEAT_REQUESTABLE = ['NONE', 'REJECTED'];

export function SeatBadge({ status }) {
  const s = SEAT_STATE[status] || SEAT_STATE.NONE;
  return (
    <span style={{ background: s.bg, color: s.fg, borderRadius: 20, padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
}
SeatBadge.propTypes = { status: PropTypes.string };

// Page shell: soft-gray canvas + a deep-blue section banner with a goldenrod
// accent. `actions` renders right-aligned header buttons.
//
// `section`/`icon` default to Entrepreneur so every existing caller is
// unchanged. They exist because the Marketing section reuses this shell — the
// styling is the shared employee-admin kit, not something specific to
// Entrepreneur, and a second copy of it would drift within a release.
export function EntPage({ title, subtitle, actions, children, section, icon }) {
  const isMobile = useIsMobile();
  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: isMobile ? '0.75rem' : '1.5rem' }}>
      <div style={{ background: brand.deepBlue, borderRadius: '12px', padding: isMobile ? '1.25rem' : '1.75rem 2rem', marginBottom: '1.25rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '3px', background: brand.goldenrod }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ background: 'rgba(208,155,44,0.15)', border: `2px solid ${brand.goldenrod}`, borderRadius: '12px', padding: '0.75rem' }}>
            <i className={`bi bi-${icon}`} style={{ color: brand.goldenrod, fontSize: '1.75rem' }} />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <p style={{ color: brand.goldenrod, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 4px' }}>{section}</p>
            <h3 style={{ color: 'white', fontWeight: 800, margin: 0, fontSize: isMobile ? '1.25rem' : undefined }}>{title}</h3>
            {subtitle && <p style={{ color: 'rgba(255,255,255,0.7)', margin: 0, fontSize: '0.85rem' }}>{subtitle}</p>}
          </div>
          {actions && <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>{actions}</div>}
        </div>
      </div>
      {children}
    </div>
  );
}
EntPage.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  actions: PropTypes.node,
  children: PropTypes.node,
  section: PropTypes.string,
  icon: PropTypes.string,
};
EntPage.defaultProps = { section: 'Entrepreneur', icon: 'lightbulb-fill' };

// Same component, honest name for callers outside the Entrepreneur section.
// The Marketing pages import this rather than `EntPage` so nothing reads as
// though Marketing lives under Entrepreneur — it doesn't, that was the point of
// splitting it out.
export const AdminPage = EntPage;

export const filterInputStyle = { border: `1px solid ${brand.softGray}`, borderRadius: 6, padding: '4px 10px', fontSize: '0.8rem', color: brand.charcoal };

// Light-themed search box + optional dropdown filters, for a white Card.Header
// (unlike ActionsLog's dark-banner search bar). Shared across the Entrepreneur
// list pages (Firms & Users, Domains, Provisioning, Limit Requests, Invoices)
// so filtering looks and behaves the same everywhere. Pass filter <select>s as
// children; search stays controlled by the caller so it can drive whatever
// client-side filtering that page needs.
export function FilterBar({ search, onSearchChange, placeholder = 'Search…', children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginLeft: 'auto' }}>
      <input
        type="search"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={placeholder}
        aria-label="Search"
        style={{ ...filterInputStyle, width: 200 }}
      />
      {children}
    </div>
  );
}
FilterBar.propTypes = {
  search: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  children: PropTypes.node,
};

// A small helper for the review pages: run a mutation, surface any reminder or
// error via a native dialog, then refetch.
export async function runAction(fn, arg, after) {
  try {
    const res = await fn(arg).unwrap();
    if (res?.reminder) window.alert(res.reminder);
    if (after) await after();
  } catch (err) {
    window.alert(err?.data?.detail || 'Action failed.');
  }
}

// A single outstanding-action row in a firm page's to-do list.
export function ActionItem({ icon, title, detail, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.85rem', padding: '0.85rem 0', borderBottom: `1px solid ${brand.softGray}` }}>
      <div style={{ flex: '0 0 auto', width: 34, height: 34, borderRadius: 8, background: 'rgba(208,155,44,0.15)', border: `1px solid ${brand.goldenrod}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <i className={`bi bi-${icon}`} style={{ color: brand.goldenrod }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: brand.deepBlue, fontSize: '0.92rem' }}>{title}</div>
        {detail && <div style={{ color: brand.charcoal, fontSize: '0.82rem', marginTop: 2 }}>{detail}</div>}
      </div>
      <div style={{ flex: '0 0 auto', display: 'flex', gap: '0.4rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>{children}</div>
    </div>
  );
}
ActionItem.propTypes = {
  icon: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  detail: PropTypes.node,
  children: PropTypes.node,
};

// The "Outstanding actions" card both firm pages open with.
export function ActionsCard({ actions, doneText }) {
  return (
    <div style={{ ...cardStyle, background: 'white', marginBottom: '1.25rem' }}>
      <div style={{ ...headStyle, padding: '0.75rem 1.25rem' }}>Outstanding actions</div>
      <div style={{ padding: actions.length ? '0.25rem 1.25rem' : '1rem 1.25rem' }}>
        {actions.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', color: brand.green, fontWeight: 600 }}>
            <i className="bi bi-check-circle-fill" style={{ fontSize: '1.25rem' }} />
            {doneText}
          </div>
        ) : actions}
      </div>
    </div>
  );
}
ActionsCard.propTypes = { actions: PropTypes.array.isRequired, doneText: PropTypes.string };

// A small labelled stat tile.
export function Fact({ label, value }) {
  return (
    <div style={{ background: 'white', borderRadius: 10, boxShadow: '0 2px 12px rgba(10,41,71,0.08)', padding: '0.6rem 1rem', minWidth: 150 }}>
      <div style={{ color: brand.charcoal, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>{label}</div>
      <div style={{ color: brand.deepBlue, fontWeight: 700 }}>{value}</div>
    </div>
  );
}
Fact.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.node };

// Under a monthly usage invoice row that was paid together with other months
// in ONE Stripe charge (see b2b_billing._annotate_combined): the single amount
// and months, so accountants can tie the rows to the card statement line.
export function CombinedChargeNote({ combined, status }) {
  if (!combined) return null;
  const when = combined.charged_at ? ` on ${new Date(combined.charged_at).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })}` : '';
  return (
    <div style={{ fontSize: '0.7rem', color: brand.tealBlue, marginTop: 2, whiteSpace: 'normal' }}>
      <i className="bi bi-link-45deg me-1" />
      {status === 'FAILED' ? 'Combined attempt' : 'Combined charge'}: {usd(combined.total_cents)}{when} for {combined.periods.join(', ')}
    </div>
  );
}
CombinedChargeNote.propTypes = {
  combined: PropTypes.shape({
    total_cents: PropTypes.number,
    periods: PropTypes.arrayOf(PropTypes.string),
    charged_at: PropTypes.string,
  }),
  status: PropTypes.string,
};
